import React, { useRef, useLayoutEffect, useState } from 'react';
import { motion } from 'motion/react';
import { BookingStatus } from '@/features/booking/types/bookingTypes';

interface StatusOverlayProps {
  status: string;
  variant: 'modal' | 'card';
}

// Status configuration for overlay display
export const getStatusConfig = (status: string) => {
  switch (status) {
    case BookingStatus.REDEEMED:
      return { text: 'REDEEMED' };
    case BookingStatus.CANCELLED_BY_USER_WITH_REFUND:
    case BookingStatus.CANCELLED_BY_USER_WITHOUT_REFUND:
    case BookingStatus.CANCELLED_BY_BUSINESS:
      return { text: 'CANCELLED' };
    case BookingStatus.EXPIRED:
      return { text: 'EXPIRED' };
    case BookingStatus.PENDING_PAYMENT:
      return { text: 'PENDING' };
    case BookingStatus.DISPUTED:
      return { text: 'ON HOLD' };
    case BookingStatus.DISPUTE_LOST:
      return { text: 'REFUNDED' };
    case BookingStatus.ACTIVE:
    default:
      return null; // No overlay for active status
  }
};

// Get status message for TicketModal
export const getStatusMessage = (status: string): string => {
  switch (status) {
    case BookingStatus.REDEEMED:
      return 'Thanks for using NightSync! We hope you had a great time.';
    case BookingStatus.CANCELLED_BY_USER_WITH_REFUND:
      return 'You are eligible for a refund, subject to cancellation fees.';
    case BookingStatus.CANCELLED_BY_USER_WITHOUT_REFUND:
      return 'Unfortunately, you cancelled too late to receive a refund.';
    case BookingStatus.CANCELLED_BY_BUSINESS:
      return 'You are eligible for a full refund.';
    case BookingStatus.EXPIRED:
      return 'This booking has expired as the event time has passed.';
    case BookingStatus.PENDING_PAYMENT:
      return 'Your booking is reserved! Complete payment to confirm.';
    case BookingStatus.DISPUTED:
      return "We've paused this ticket due to an ongoing payment dispute with your bank. Please contact support if you believe this is an error.";
    case BookingStatus.DISPUTE_LOST:
      return 'This transaction has been reversed and the funds returned to you. This ticket is no longer valid for entry.';
    case BookingStatus.ACTIVE:
    default:
      return 'Present this QR code at the entrance';
  }
};

// Check if booking status should show empty/abandoned state
export const isAbandonedStatus = (status: string): boolean => {
  return status === BookingStatus.ABANDONED;
};

// All possible status texts for calculating max width
const ALL_STATUS_TEXTS = ['REDEEMED', 'CANCELLED', 'EXPIRED', 'PENDING', 'ON HOLD', 'REFUNDED'];

export const StatusOverlay: React.FC<StatusOverlayProps> = ({ status, variant }) => {
  const config = getStatusConfig(status);
  const containerRef = useRef<HTMLDivElement>(null);
  const [fontSize, setFontSize] = useState(32);
  const [diagonalAngle, setDiagonalAngle] = useState(-45);
  
  const isCard = variant === 'card';
  const isModal = variant === 'modal';
  
  // Fixed font size for modal variant (watermark style)
  const MODAL_FIXED_FONT_SIZE = 68;
  const MODAL_FIXED_ANGLE = -25; // Slight angle for watermark effect

  // Calculate font size and diagonal angle based on container dimensions
  useLayoutEffect(() => {
    if (!config) return;
    
    // For modal variant, use fixed values
    if (isModal) {
      setFontSize(MODAL_FIXED_FONT_SIZE);
      setDiagonalAngle(MODAL_FIXED_ANGLE);
      return;
    }
    
    const calculateDimensions = () => {
      if (!containerRef.current) return;
      
      const container = containerRef.current;
      const containerWidth = container.offsetWidth;
      const containerHeight = container.offsetHeight;
      
      // Calculate the true diagonal angle of the container
      // atan(height/width) gives angle from horizontal to diagonal
      // Negative because we want top-right to bottom-left direction
      const angleRadians = Math.atan(containerHeight / containerWidth);
      const angleDegrees = -(angleRadians * 180 / Math.PI);
      setDiagonalAngle(angleDegrees);
      
      // Calculate the diagonal length of the container
      const diagonalLength = Math.sqrt(containerWidth ** 2 + containerHeight ** 2);
      
      // Find the longest status text
      const longestText = ALL_STATUS_TEXTS.reduce((a, b) => a.length > b.length ? a : b);
      
      // Create a temporary span to measure text width
      const tempSpan = document.createElement('span');
      tempSpan.style.fontFamily = 'Roboto, system-ui';
      tempSpan.style.fontWeight = '700';
      tempSpan.style.letterSpacing = '0.1rem';
      tempSpan.style.visibility = 'hidden';
      tempSpan.style.position = 'absolute';
      tempSpan.style.whiteSpace = 'nowrap';
      tempSpan.textContent = longestText;
      document.body.appendChild(tempSpan);
      
      // Binary search for optimal font size
      // Card: use 85% of diagonal for watermark effect
      const targetWidth = diagonalLength * 0.85;
      let low = 16;
      let high = 300; // Allow very large fonts for cards
      let optimalSize = 32;
      
      while (low <= high) {
        const mid = Math.floor((low + high) / 2);
        tempSpan.style.fontSize = `${mid}px`;
        const textWidth = tempSpan.offsetWidth;
        
        if (textWidth <= targetWidth) {
          optimalSize = mid;
          low = mid + 1;
        } else {
          high = mid - 1;
        }
      }
      
      document.body.removeChild(tempSpan);
      setFontSize(optimalSize);
    };
    
    calculateDimensions();
    
    // Recalculate on resize (only for card variant)
    const resizeObserver = new ResizeObserver(calculateDimensions);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    
    return () => resizeObserver.disconnect();
  }, [config, isCard, isModal]);

  // Don't render overlay for active status or if no config
  if (!config) {
    return null;
  }

  return (
    <motion.div
      ref={containerRef}
      className="absolute inset-0 flex items-center justify-center pointer-events-none"
      style={{ zIndex: 15 }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
    >
      {/* Watermark text stamp */}
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1, rotate: diagonalAngle }}
        transition={{ delay: 0.15, duration: 0.5, ease: 'easeOut' }}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'absolute',
        }}
      >
        <span
          className="select-none whitespace-nowrap"
          style={{
            fontFamily: 'Roboto, system-ui',
            fontWeight: 700,
            fontSize: `${fontSize}px`,
            letterSpacing: '0.02em',
            lineHeight: 1,
            // Stretch text vertically for watermark effect
            transform: 'scaleY(1.3)',
            // Shiny metallic gradient effect
            background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(200,205,215,0.6) 25%, rgba(255,255,255,0.8) 50%, rgba(200,205,215,0.6) 75%, rgba(180,185,195,0.4) 100%)',
            WebkitBackgroundClip: 'text',
            backgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            // Subtle depth shadow for polish effect
            filter: 'drop-shadow(1px 1px 0px rgba(0,0,0,0.15)) drop-shadow(-0.5px -0.5px 0px rgba(255,255,255,0.3))',
            paintOrder: 'stroke fill',
            // Ink stamp texture effect for authentic look
            WebkitMaskImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.5\' numOctaves=\'3\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\'/%3E%3C/svg%3E")',
            maskImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.5\' numOctaves=\'3\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\'/%3E%3C/svg%3E")',
          }}
        >
          {config.text}
        </span>
      </motion.div>
    </motion.div>
  );
};
